import {
  downloadActivities,
  downloadActivitiesRaw,
  downloadPatientActivities,
  downloadPatientActivitiesRaw,
} from '@/api/activities';
import { runtimeConfig } from '@/config/runtime';
import type { Activity, DownloadActivityRequest } from '@/types/activity';
import type { DownloadStructure } from '@/types/download';
import type { Patient } from '@/types/patient';
import { extractTokenFromUrl } from '@/utils/download';

export type DownloadMode = 'processed' | 'raw';

type DoctorDownloadAction = (
  request: DownloadActivityRequest,
  structure: DownloadStructure
) => Promise<Blob>;
type PatientDownloadAction = (
  activityIds: string[],
  structure: DownloadStructure
) => Promise<Blob>;

const RAW_DATA_DISABLED_ERROR = 'Raw data download is disabled';

function withRawDataDownloadGuard<TArgs extends unknown[]>(
  action: (...args: TArgs) => Promise<Blob>
): (...args: TArgs) => Promise<Blob> {
  return async (...args: TArgs) => {
    if (!runtimeConfig.rawDataDownloadEnabled) {
      throw new Error(RAW_DATA_DISABLED_ERROR);
    }

    return action(...args);
  };
}

export interface DoctorDownloadModeConfig {
  loadActivities: DownloadMode;
  downloadAction: DoctorDownloadAction;
  filenamePrefix: string;
  downloadLabel: string;
  cardDescription: string;
}

export interface PatientDownloadModeConfig {
  downloadAction: PatientDownloadAction;
  filenamePrefix: string;
  downloadErrorMessage: string;
  cardDescription: string;
  resultLabel: '' | 'raw';
}

export function getDoctorDownloadModeConfig(mode: DownloadMode): DoctorDownloadModeConfig {
  if (mode === 'raw') {
    return {
      loadActivities: 'raw',
      downloadAction: withRawDataDownloadGuard(downloadActivitiesRaw),
      filenamePrefix: 'nushu-activities-raw',
      downloadLabel: 'raw activities',
      cardDescription: 'Select patients and filter activities to download raw data in bulk',
    };
  }

  return {
    loadActivities: 'processed',
    downloadAction: downloadActivities,
    filenamePrefix: 'nushu-activities',
    downloadLabel: 'activities',
    cardDescription: 'Select patients and filter activities to download',
  };
}

export function getPatientDownloadModeConfig(mode: DownloadMode): PatientDownloadModeConfig {
  if (mode === 'raw') {
    return {
      downloadAction: withRawDataDownloadGuard(downloadPatientActivitiesRaw),
      filenamePrefix: 'my-activities-raw',
      downloadErrorMessage: 'Failed to download raw activities',
      cardDescription: 'Filter activities to download raw data',
      resultLabel: 'raw',
    };
  }

  return {
    downloadAction: (activityIds, structure) =>
      downloadPatientActivities(activityIds, undefined, structure),
    filenamePrefix: 'my-activities',
    downloadErrorMessage: 'Failed to download activities',
    cardDescription: 'Filter activities to download',
    resultLabel: '',
  };
}

export function getSelectedActivityIds(activities: Activity[]): string[] {
  return activities.map((activity) => activity.activityStreamId || activity.id);
}

export function buildDoctorDownloadRequest(
  selectedActivities: Activity[],
  allPatients: Patient[]
): { request: DownloadActivityRequest; uniquePatientCount: number } {
  const uniquePatientCount = new Set(selectedActivities.map((activity) => activity.patientId)).size;
  const patientActivityMap = new Map<string, { token: string; activities: string[] }>();

  selectedActivities.forEach((activity) => {
    const patient = allPatients.find((item) => item.id === activity.patientId);
    if (!patient) return;

    if (!patientActivityMap.has(patient.id)) {
      patientActivityMap.set(patient.id, {
        token: extractTokenFromUrl(patient.apiToken),
        activities: [],
      });
    }

    patientActivityMap.get(patient.id)?.activities.push(activity.activityStreamId || activity.id);
  });

  return {
    request: {
      data: Array.from(patientActivityMap.entries()).map(([userId, data]) => ({
        token: data.token,
        userId,
        activities: data.activities,
      })),
    },
    uniquePatientCount,
  };
}

export function formatPatientDownloadSuccessMessage(
  selectedCount: number,
  resultLabel: '' | 'raw'
): string {
  const activityLabel = selectedCount === 1 ? 'activity' : 'activities';
  return `Downloaded ${selectedCount} ${resultLabel} ${activityLabel}`.replace(/\s+/g, ' ').trim();
}
